import React from 'react';
import { FormControl, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';

type StyledTextFieldProps = TextFieldProps & {
    valid?: boolean;
};

const TextFieldStyled = styled(TextField)<StyledTextFieldProps>(({ theme, valid }) => ({
    height: '50px',
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            color: theme.palette.primary.light,
            borderRadius: '10px',
            borderColor: theme.palette.border.light,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.border.main,
            color: theme.palette.primary.light,
        },
        '&.Mui-focused fieldset': {
            borderWidth: '1px',
            borderColor: theme.palette.border.main,
            color: theme.palette.primary.light,
        },
        '&.Mui-disabled fieldset': {
            borderColor: theme.palette.action.disabled,
        },
    },
    '& .MuiInputBase-root': {
        color: theme.palette.primary.main,
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            WebkitTextFillColor: theme.palette.text.disabled, // needed for Chrome autofill + disabled
        },
    },
    '& .MuiInputLabel-outlined': {
        color: theme.palette.primary.main,
        opacity: 0.8,
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
        },
    },
}));

interface EnhancedTextFieldProps {
    value: any;
    onChange: (value: any) => void;
    disabled: boolean;
    label: string;
    fullWidth?: boolean;
    type?: string;
    valid?: boolean;
    icon?: React.ReactNode;
    id?: string;
    name?: string;
    autoComplete?: string;
}

const TextFieldComponent: React.FC<EnhancedTextFieldProps> = (props) => {
    // Unique per-instance id so the browser doesn't treat every text field as the
    // same one and autofill a value across unrelated fields (#971). Callers can
    // override with an explicit id/name.
    const generatedId = React.useId();
    const fieldId = props.id ?? generatedId;

    const adornments = (
        <>
            {props.valid && <CheckIcon sx={{ color: 'var(--color-primary)' }} />}
            {props.icon && props.icon}
        </>
    );

    return (
        <FormControl fullWidth={props.fullWidth ?? false}>
            <TextFieldStyled
                id={fieldId}
                name={props.name}
                autoComplete={props.autoComplete ?? 'off'}
                label={props.label}
                value={props.value}
                disabled={props.disabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => props.onChange(event.target.value)}
                variant={'outlined'}
                type={props.type ?? undefined}
                InputProps={{
                    ...(props.type === 'number' ? { inputProps: { min: 1 } } : {}),
                    endAdornment: props.valid || props.icon ? <InputAdornment position={'end'}>{adornments}</InputAdornment> : null,
                }}
                // @ts-ignore - valid is a custom prop for styling
                valid={props.valid ? true : undefined}
            />
        </FormControl>
    );
};

export default TextFieldComponent;
